import React from "react";
import { Button, List, Tooltip, Typography } from "antd";
import useOrganization from "../../../../Hooks/useOrganization";
import { Interview } from "../../../../Entities/Interview";
import dayjs from "dayjs";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  HourglassOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { openNotification } from "../../../../Components/Notification";

interface JobApplicationScheduledInterviewMeetingDetailsProps {
  scheduledInterview: Interview;
}

const JobApplicationScheduledInterviewMeetingDetails: React.FC<
  JobApplicationScheduledInterviewMeetingDetailsProps
> = ({ scheduledInterview }) => {
  const { org } = useOrganization();

  if (!org) {
    return null;
  }

  const { startTime, endTime, conferenceId } = scheduledInterview;

  const interviewDate = dayjs(startTime)
    .tz(org?.timeZone)
    .format("ddd DD MMM YYYY");
  const interviewStartTime = dayjs(startTime)
    .tz(org?.timeZone)
    .format("h:mm A");
  const interviewEndTime = dayjs(endTime).tz(org?.timeZone).format("h:mm A");
  const interviewDuration = dayjs(endTime).diff(dayjs(startTime), "minutes");
  const interviewTimeZone = org?.timeZone;
  const interviewMeetingLink = `https://meet.google.com/${conferenceId}`;

  const isMeetingLinkExpired = dayjs(startTime).isBefore(dayjs());

  const data = [
    {
      title: "Date",
      icon: <CalendarOutlined />,
      description: <Typography.Text>{interviewDate}</Typography.Text>,
    },
    {
      title: "Timing",
      icon: <ClockCircleOutlined />,
      description: (
        <Typography.Text>
          {interviewStartTime} - {interviewEndTime}
        </Typography.Text>
      ),
    },
    {
      title: "Duration (minutes)",
      icon: <HourglassOutlined />,
      description: <Typography.Text>{interviewDuration}</Typography.Text>,
    },
    {
      title: "Timezone",
      icon: <GlobalOutlined />,
      description: <Typography.Text>{interviewTimeZone}</Typography.Text>,
    },
  ];

  const handleCopyMeetingLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(interviewMeetingLink);
        openNotification(
          "success",
          "Meeting link copied",
          "The meeting link has been copied to your clipboard."
        );
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = interviewMeetingLink;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        openNotification(
          "success",
          "Meeting link copied",
          "The meeting link has been copied to your clipboard."
        );
      } else {
        throw new Error("Copy command failed");
      }
    } catch (error) {
      console.error("Failed to copy meeting link:", error);
      openNotification(
        "error",
        "Failed to copy meeting link",
        "Please copy the link manually from above."
      );
    }
  };

  return (
    <div className="job-application-scheduled-interview-section-container">
      <div className="job-application-scheduled-interview-section-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Meeting Details
        </Typography.Title>
      </div>

      <div className="job-application-scheduled-interview-meeting-details-container">
        <List
          size="small"
          itemLayout="horizontal"
          dataSource={data}
          renderItem={(item, index) => (
            <List.Item key={index}>
              <List.Item.Meta
                avatar={item.icon}
                title={<Typography.Text strong>{item.title}</Typography.Text>}
                description={item.description}
              />
            </List.Item>
          )}
        />

        <div className="job-application-scheduled-interview-meeting-details-link-container">
          <Tooltip
            title={
              isMeetingLinkExpired
                ? "Meeting link expired"
                : "Click to join the meeting"
            }
          >
            <Typography.Link
              style={{ fontSize: 14 }}
              href={interviewMeetingLink}
              target="_blank"
              disabled={isMeetingLinkExpired}
            >
              {interviewMeetingLink}
            </Typography.Link>
          </Tooltip>

          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={handleCopyMeetingLink}
          />
        </div>
      </div>
    </div>
  );
};

export default JobApplicationScheduledInterviewMeetingDetails;
